import {
  AUTHORIZATION_AUTH_TYPES,
  BODY_TYPES,
  HTTP_REQUEST_METHODS,
  RequestSection,
  RESPONSE_FIELD
} from '@/utils/apiTab'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface ParamsField {
  key: string
  value: string
  description: string
  active: boolean
}

export interface FormDataKeyType {
  File: 'File'
  Text: 'Text'
}

export interface ParamsFieldBodyFromData {
  key: string
  value: string
  description: string
  active: boolean
  valueType: string
  selectedFile?: File
}

export interface AuthorizationField {
  selected: AUTHORIZATION_AUTH_TYPES
  basicAuth?: { userName: string; passWord: string }
  bearerToken?: { token: string }
  jwtBearer?: { tokenAddToField: string; algorithm: string; secret: string; payload: string }
}

export interface BodyRawField {
  rawContentType: string
  rawValue: string
}

export interface BodyField {
  selected: BODY_TYPES
  formData?: ParamsFieldBodyFromData[]
  xWwwFormUrlencoded?: ParamsField[]
  raw?: BodyRawField
}

export interface RequestField {
  id: number // Unique identifier for each request
  Params: ParamsField[]
  Authorization: AuthorizationField
  Headers: ParamsField[]
  Body: BodyField
}

export interface activeTabRequest {
  id: number
  active: boolean
  url?: string
  method?: string
  responseField?: string
}

export const useApiTabStore = defineStore('apiTab', () => {
  const activeTabRequests = ref<activeTabRequest[]>([])
  const requestFields = ref<RequestField[]>([])
  const canUpdateParam = ref<boolean>(false)
  const canUpdateUrl = ref<boolean>(false)

  /**
   * Generates a unique identifier based on the current timestamp and a random number.
   * @returns A unique identifier.
   */
  const generateUniqueId = () => {
    return Date.now() + Math.floor(Math.random() * 1000)
  }

  /**
   * Retrieves the URL of the active request corresponding to the given ID.
   * @param id - The unique identifier of the request whose URL is to be retrieved.
   * @returns The URL of the request if found; otherwise, `undefined`.
   */
  const getRequestUrl = (id: number) => {
    const activeTabField = activeTabRequests.value.find((field) => field.id === id)

    if (activeTabField) {
      return activeTabField.url
    }
  }

  /**
   * Retrieves the specified field data for a request identified by the given ID.
   * @param id - The unique identifier of the request field to retrieve data from.
   * @param  type - The type of data to retrieve (e.g., parameters, headers, body).
   * @param authType - Optional authorization type for retrieving authorization-specific data.
   * @returns  The requested field data, or `undefined` if not found.
   */
  const getRequestFieldData = (id: number, type: string, authType?: string | null) => {
    const requestField = requestFields.value.find((field) => field.id === id)

    if (requestField) {
      switch (type) {
        case RequestSection.PARAMS:
          return requestField.Params
        case RequestSection.AUTHORIZATION:
          if (!authType) {
            return requestField.Authorization.selected
          } else if (authType == AUTHORIZATION_AUTH_TYPES.BASIC_AUTH) {
            return requestField.Authorization.basicAuth
          } else if (authType == AUTHORIZATION_AUTH_TYPES.BEARER_TOKEN) {
            return requestField.Authorization.bearerToken?.token
          } else if (authType == AUTHORIZATION_AUTH_TYPES.JWT_BEARER) {
            return requestField.Authorization.jwtBearer
          }
          break

        case RequestSection.HEADERS:
          return requestField.Headers
        case RequestSection.BODY:
          if (!authType) {
            return requestField.Body.selected
          } else if (authType == BODY_TYPES.FORM_DATA) {
            return requestField.Body.formData
          } else if (authType == BODY_TYPES.FORM_URLENCODED) {
            return requestField.Body.xWwwFormUrlencoded
          } else if (authType == BODY_TYPES.RAW_DATA) {
            return requestField.Body.raw
          }
          break

        default:
      }
    }
  }

  /**
   * Updates a specific field of a request identified by the given ID.
   * @param id - The unique identifier of the request field to update.
   * @param type - The type of data to update (e.g., parameters, headers, body).
   * @param value - The new value to assign to the specified request field.
   * @param authType - Optional authorization type for updating authorization-specific data.
   */
  const updateRequestField = (id: number, type: string, value: any, authType?: string | null) => {
    const requestField = requestFields.value.find((field) => field.id === id)
    if (requestField) {
      switch (type) {
        case RequestSection.PARAMS:
          requestField.Params = value
          break
        case RequestSection.AUTHORIZATION:
          if (!authType) {
            requestField.Authorization.selected = value
          } else if (authType == AUTHORIZATION_AUTH_TYPES.BASIC_AUTH) {
            requestField.Authorization.basicAuth = {
              userName: value.userName,
              passWord: value.passWord
            }
          } else if (authType == AUTHORIZATION_AUTH_TYPES.BEARER_TOKEN) {
            requestField.Authorization.bearerToken = {
              token: value
            }
          } else if (authType == AUTHORIZATION_AUTH_TYPES.JWT_BEARER) {
            requestField.Authorization.jwtBearer = {
              tokenAddToField: value.jwtAddToRequest,
              algorithm: value.jwtAlgorithm,
              secret: value.jwtSecret,
              payload: value.jwtPayload
            }
          }

          break
        case RequestSection.HEADERS:
          requestField.Headers = value
          break
        case RequestSection.BODY:
          if (!authType) {
            requestField.Body.selected = value
          } else if (authType == BODY_TYPES.FORM_DATA) {
            requestField.Body.formData = value
          } else if (authType == BODY_TYPES.FORM_URLENCODED) {
            requestField.Body.xWwwFormUrlencoded = value
          } else if (authType == BODY_TYPES.RAW_DATA) {
            requestField.Body.raw = {
              rawContentType: value.rawContentType,
              rawValue: value.rawValue
            }
          }

          break
        default:
      }
    }
  }
  /**
   * Adds a new request field to the requestFields array with a specified ID.
   * @param id - The unique identifier for the new request field.
   */
  const addNewRequest = (id: number) => {
    const newRequest: RequestField = {
      id: id,
      Params: [],
      Authorization: { selected: AUTHORIZATION_AUTH_TYPES.NO_AUTH },
      Headers: [],
      Body: { selected: BODY_TYPES.FORM_DATA }
    }

    requestFields.value.push(newRequest)
  }
  /**
   * Creates a new active tab request and deactivates all existing active requests.
   */
  const setCreateActiveTabRequests = () => {
    const uniqueId = generateUniqueId()
    activeTabRequests.value &&
      activeTabRequests.value.forEach((request) => {
        request.active = false
      })
    const newObj = {
      id: uniqueId,
      active: true,
      method: HTTP_REQUEST_METHODS.GET,
      responseField: RESPONSE_FIELD.PARAMS
    }

    activeTabRequests.value.push(newObj)

    addNewRequest(uniqueId)
  }
  /**
   * Sets the specified request as active and deactivates all other requests.
   * @param  id - The unique identifier of the request to be set as active.
   */
  const setActiveRequest = (id: number) => {
    activeTabRequests.value &&
      activeTabRequests.value.forEach((request) => {
        request.active = false
      })

    const requestToActivate =
      activeTabRequests.value && activeTabRequests.value.find((request) => request.id === id)
    if (requestToActivate) {
      requestToActivate.active = true
    }
  }
  /**
   * Updates the URL of the request identified by the given ID.
   * @param id - The unique identifier of the request whose URL is to be updated.
   * @param url - The new URL to assign to the specified request.
   */
  const updateActiveRequestUrl = (id: number, url: string) => {
    const requestToActivate =
      activeTabRequests.value && activeTabRequests.value.find((request) => request.id === id)
    if (requestToActivate) {
      requestToActivate.url = url
    }
  }

  /**
   * Updates the HTTP method of the request identified by the given ID.
   * @param id - The unique identifier of the request whose method is to be updated.
   * @param method - The new HTTP method to assign to the specified request.
   */
  const updateActiveRequestMethod = (id: number, method: string) => {
    const requestToActivate =
      activeTabRequests.value && activeTabRequests.value.find((request) => request.id === id)

    if (requestToActivate) {
      requestToActivate.method = method
    }
  }
  /**
   * Updates the response field of the request identified by the given ID.
   * @param id - The unique identifier of the request whose response field is to be updated.
   * @param method - The new response field to assign to the specified request.
   */
  const updateActiveRequestField = (id: number, method: string) => {
    const requestToActivate =
      activeTabRequests.value && activeTabRequests.value.find((request) => request.id === id)

    if (requestToActivate) {
      requestToActivate.responseField = method
    }
  }
  /**
   * Removes a request identified by the given ID from the active tab requests and request fields.
   * @param  id - The unique identifier of the request to be removed.
   */
  const removeRequest = (id: number) => {
    const requestIndex = activeTabRequests.value.findIndex((request) => request.id === id)

    if (requestIndex >= 0 && requestIndex < activeTabRequests.value.length - 1) {
      const nextRequest = activeTabRequests.value[requestIndex + 1]
      setActiveRequest(nextRequest.id)
    }

    activeTabRequests.value = activeTabRequests.value.filter((request) => request.id !== id)
    requestFields.value = requestFields.value.filter((request) => request.id !== id)
  }
  /**
   * Retrieves all required data for a given ID by filtering `requestFields`
   * and `activeTabRequests`. If the data is found, it returns an object
   * containing the data along with its associated HTTP method and URL.
   * @param  id - The ID of the field to retrieve data for.
   * @returns An object containing the field data along with
   *                             the associated HTTP method and URL if found,
   *                             or undefined if no data is found.
   */
  const getAllRequiredData = (id: number) => {
    const data = requestFields.value.filter((field) => field.id === id)
    const methodAndUrlDetails = activeTabRequests.value.filter((field) => field.id === id)

    if (data.length > 0) {
      const methodDetail =
        methodAndUrlDetails.length > 0
          ? methodAndUrlDetails[0]
          : { method: undefined, url: undefined }

      // Concatenating method and url to the data object
      return {
        ...data[0],
        method: methodDetail.method,
        url: methodDetail.url
      }
    }

    return undefined
  }
  /**
   * Sets the `canUpdateParam` value to the specified boolean, controlling whether parameters can be updated.
   * @param  val - The new value to set for `canUpdateParam`.
   */
  const setCanUpdateParam = (val: boolean) => {
    canUpdateParam.value = val
  }
  /**
   * Sets the `canUpdateUrl` value to the specified boolean, controlling whether the URL can be updated.
   * @param val - The new value to set for `canUpdateUrl`.
   */
  const setCanUpdateUrl = (val: boolean) => {
    canUpdateUrl.value = val
  }

  return {
    requestFields,
    updateRequestField,
    addNewRequest,
    activeTabRequests,
    setCreateActiveTabRequests,
    setActiveRequest,
    getRequestFieldData,
    updateActiveRequestUrl,
    updateActiveRequestMethod,
    updateActiveRequestField,
    getRequestUrl,
    removeRequest,
    getAllRequiredData,
    canUpdateParam,
    canUpdateUrl,
    setCanUpdateParam,
    setCanUpdateUrl
  }
})
